import type { Dispatch, SetStateAction } from "react";
import { t } from "@/i18n-js/instance";
import { CourseTypePickerLabel } from "@/react/components/Spaces/CourseSpace/CourseTypePicker/CourseTypePickerLabel";
import { Section } from "@/react/components/Spaces/CustomizeSpaceFullPage/Section";
import { useBooleanList } from "@/react/hooks/utils/useBooleanList";
import type { CoursesLesson } from "@/react/types/CoursesLesson";
import type { CoursesSection } from "@/react/types/CoursesSection";
import type { Space } from "@/react/types/Space";
import { LoaderRow } from "@circle-react/components/shared/LoaderRow";
import { Typography } from "@circle-react-uikit/Typography";
import { EmptyState } from "./EmptyState";
import { LessonTable } from "./Table";

// Extended section type with sortId for drag and drop functionality
interface SectionWithSortId extends CoursesSection {
  lessons: (CoursesLesson & { sortId: string })[];
  sortId: string;
}

interface CustomizeLessonsContentProps {
  sections: SectionWithSortId[];
  sectionsAreLoading: boolean;
  setSections: Dispatch<SetStateAction<SectionWithSortId[]>>;
  space: Space;
}

export const CustomizeLessonsContent = ({
  sections,
  setSections,
  sectionsAreLoading,
  space,
}: CustomizeLessonsContentProps) => {
  const [sectionsNameEditor, dispatchSectionsNameEditor] = useBooleanList();

  return (
    <Section>
      <div className="flex flex-col">
        <div
          className="mb-4 flex justify-between"
          data-testid="customize-lessons-header"
        >
          <div className="space-y-2">
            <Typography.TitleSm weight="bold">
              {t("courses.edit.sections.lessons")}
            </Typography.TitleSm>
            <CourseTypePickerLabel space={space} />
          </div>
        </div>
        {sectionsAreLoading ? (
          <LoaderRow />
        ) : sections.length ? (
          <LessonTable
            space={space}
            sections={sections}
            setSections={setSections}
            dispatchSectionsNameEditor={dispatchSectionsNameEditor}
            sectionsNameEditor={sectionsNameEditor}
          />
        ) : (
          <EmptyState
            sections={sections}
            dispatchSectionsNameEditor={dispatchSectionsNameEditor}
          />
        )}
      </div>
    </Section>
  );
};
